import { useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Inbox, BellRing, LogOut, ShieldAlert, ScrollText, Lightbulb } from 'lucide-react';
import { cn, Button } from '@dgm/ui';
import { useAuth } from '../lib/auth.js';

const NAV = [
  { to: '/dashboard', label: 'Overview', icon: LayoutDashboard },
  { to: '/users', label: 'Users', icon: Users },
  { to: '/tickets', label: 'Tickets', icon: Inbox },
  { to: '/audit', label: 'Audit Log', icon: ScrollText },
  { to: '/insights', label: 'Insights', icon: Lightbulb },
  { to: '/alarms', label: 'Alarms', icon: BellRing },
];

/** Operator-gated admin shell. Hydrates session, enforces OperatorAdmins. */
export function AdminShell() {
  const { status, user, hydrate, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (status === 'loading') void hydrate();
  }, [status, hydrate]);

  useEffect(() => {
    if (status === 'anonymous') navigate('/login', { replace: true });
  }, [status, navigate]);

  if (status === 'loading' || status === 'anonymous') {
    return <div className="flex min-h-full items-center justify-center text-muted-foreground">Loading…</div>;
  }

  if (status === 'forbidden') {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-4 p-8 text-center">
        <ShieldAlert className="h-10 w-10 text-destructive" />
        <h1 className="text-xl font-semibold">Operator access required</h1>
        <p className="max-w-sm text-muted-foreground">
          Your account ({user?.email}) isn't a member of the OperatorAdmins group.
        </p>
        <Button variant="outline" onClick={() => logout().then(() => navigate('/login'))}>
          Sign out
        </Button>
      </div>
    );
  }

  return (
    <div className="grid min-h-full grid-cols-[220px_1fr]">
      <aside className="flex flex-col border-r bg-muted/20 p-4">
        <div className="mb-6 flex items-center gap-2 px-2 font-bold text-primary">
          <ShieldAlert className="h-5 w-5" /> dgm · admin
        </div>
        <nav className="flex flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
                location.pathname.startsWith(to) && 'bg-accent text-accent-foreground',
              )}
            >
              <Icon className="h-4 w-4" /> {label}
            </Link>
          ))}
        </nav>
        <div className="mt-auto">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => logout().then(() => navigate('/login'))}
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>
      <div className="flex flex-col">
        <header className="flex h-14 items-center justify-between border-b px-6">
          <span className="text-sm text-muted-foreground">Operator console</span>
          <span className="text-sm font-medium">{user?.email}</span>
        </header>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
